import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import DashboardTable from '../../components/DashboardTable';
import PageStack from '../../components/PageStack';
import { LAYOUT } from '../../theme/layoutTokens';
import { attributesApi } from '../../api/attributes.api';
import { Attribute } from '../../types/attribute';

const AttributeList: React.FC = () => {
  const navigate = useNavigate();
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAttributes();
  }, []);

  const loadAttributes = async () => {
    try {
      setLoading(true);
      const data = await attributesApi.getAll();
      setAttributes(data);
    } catch (error) {
      console.error('Error loading attributes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reference: string) => {
    if (window.confirm('¿Eliminar este atributo?')) {
      try {
        await attributesApi.delete(reference);
        setAttributes((prev) => prev.filter((a) => a.reference !== reference));
      } catch (error) {
        console.error('Error deleting attribute:', error);
      }
    }
  };

  return (
    <PageStack spacing={LAYOUT.stackSurfaceGap}>
      <DashboardPageHeader
        title="Atributos"
        action={
          <Button color="primary" onClick={() => navigate('/attributes/new')}>
            Nuevo Atributo
          </Button>
        }
      />

      <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'md', boxShadow: 'sm', p: 0 }}>
        {attributes.length === 0 ? (
          <Typography level="body-sm" sx={{ py: 4, textAlign: 'center', color: 'neutral.500' }}>
            No hay atributos creados aún.
          </Typography>
        ) : (
          <DashboardTable stickyHeader>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Código</th>
                <th>Tipo</th>
                <th>Variante</th>
                <th>Filtrable</th>
                <th style={{ textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {attributes.map((attr) => (
                <tr key={attr.reference}>
                  <td>{attr.name}</td>
                  <td>
                    <Typography level="body-sm" sx={{ fontFamily: 'monospace', fontSize: 'xs' }}>
                      {attr.code}
                    </Typography>
                  </td>
                  <td>{attr.dataType}</td>
                  <td>{attr.isVariantAxis ? 'Sí' : 'No'}</td>
                  <td>{attr.isFilterable ? 'Sí' : 'No'}</td>
                  <td>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="primary"
                        onClick={() => navigate(`/attributes/${attr.reference}/edit`)}
                      >
                        <EditRoundedIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleDelete(attr.reference)}
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </DashboardTable>
        )}
      </Sheet>
    </PageStack>
  );
};

export default AttributeList;
